/**
 * Wire DTOs for an employee profile: the `/profiles/:userId` page and the
 * chat side panel a person card opens. Both render the same SDK components
 * (`@magnus/react/profile`), so the shapes live here rather than in the
 * CMS package the SDK cannot import.
 *
 * The server half is `@workspace/cms` (`resolveProfileFull`), which
 * re-exports these from `read/profile-wire.ts`.
 */

/**
 * Longest bio, in characters.
 *
 * Three places have to agree: the `users.bio` field's `maxLength`, the
 * router's zod schema, and the editor's counter — the same rule
 * `ENGAGEMENT_BODY_MAX` keeps for comments and feedback.
 */
export const PROFILE_BIO_MAX = 2000;

/**
 * The `/profiles/:userId` id that means "whoever is viewing": `/profiles/me`
 * is the viewer's own profile. It is how a link that has no directory id at
 * hand — the user menu, the `/settings` redirect — reaches the profile.
 * Row ids are integers, so it cannot collide with a real one.
 */
export const SELF_PROFILE_ID = "me";

/**
 * A person's profile page: `/profiles/:userId`. `null` for a missing or
 * blank id, so a caller links only when there is someone to link to.
 *
 * The id is the Payload `users` row id — the integer the admin URL shows
 * (`/publisher/admin/collections/users/7` → `/profiles/7`) — not the Entra
 * object id and not `magnusUserId`. The row id is the key mentions and
 * person cards already carry, and the Entra id is a grant-restricted field
 * that should not leak into a URL.
 *
 * Stable in prod, where the database persists. Not stable across disposable
 * local/test databases: a reseed re-mints row ids, so a `/profiles/7` link
 * from yesterday's local DB can point at someone else or 404 today.
 */
export function profileHref(
  userId: string | number | null | undefined
): string | null {
  const id = String(userId ?? "").trim();
  return id ? `/profiles/${encodeURIComponent(id)}` : null;
}

/** The viewer's own profile, `/profiles/me`. */
export const SELF_PROFILE_HREF = `/profiles/${SELF_PROFILE_ID}`;

/**
 * The person a profile is about: the CMS reader's person-card DTO
 * (`ReaderPersonCard`) minus its mention-only keys (`fallback`,
 * `groupLabel`) and the grant-restricted `entraObjectId`, which the profile
 * read never projects. Pinned both ways by
 * `packages/cms/src/__tests__/profile-contract-drift-check.ts`.
 */
export type ProfilePerson = {
  name: string;
  email?: string;
  jobTitle?: string;
  office?: string;
  phone?: string;
  /** The CMS directory row; the profile route is `/profiles/:userId`. */
  userId?: string;
  roleLabel?: string;
  titleOverride?: string;
  officeOverride?: string;
};

/**
 * The title to print under a person's name: an editor's role label, then a
 * title override, then the directory title. The CMS person card's
 * `personCardTitle` delegates here, so the two cannot disagree.
 */
export function profilePersonTitle(
  person: Pick<ProfilePerson, "roleLabel" | "titleOverride" | "jobTitle">
): string | null {
  return person.roleLabel ?? person.titleOverride ?? person.jobTitle ?? null;
}

/** One person in the Reporting card / org-chart drawer. */
export type ProfileOrgPerson = {
  name: string;
  jobTitle?: string;
  /** Directory email, when known — also the Graph photo key. */
  email?: string;
  /**
   * CMS directory id behind the person's `/profiles/:userId` link. Absent
   * for someone Graph knows but the directory does not (or someone
   * deprovisioned — their profile route 404s, so no link is emitted).
   */
  userId?: string;
  /**
   * Graph photo as a data URL (48x48), when one was resolved. Eager only
   * for the card's preview rows; the org drawer fills the rest lazily on
   * open (`reader.profile.orgPhotos`), so a missing photo means initials,
   * not a missing person.
   */
  photo?: string;
};

export type ProfileReporting = {
  manager: ProfileOrgPerson | null;
  reports: ProfileOrgPerson[];
};

/**
 * HR details from the Databricks employee table, read through the knowledge
 * index's nightly employee projection — the same row chat's employee cards
 * resolve, so profile and chat cannot drift apart.
 */
export type ProfileOrgDetails = {
  division?: string;
  region?: string;
  jobFunction?: string;
  officeLocation?: string;
  /**
   * The warehouse's official YearsOfService, never computed from a start
   * date — tenure badges and "N years to the next badge" both key off it.
   */
  yearsOfService?: number;
  /** Day and month only; the warehouse carries no birth year. */
  birthDay?: number;
  birthMonth?: number;
};

/** One row of the owner-only Authoring feed. */
export type ProfileAuthoredPage = {
  /** Stable key: the page's row id. Titles repeat and drafts have no href. */
  id: string;
  title: string;
  href: string | null;
  isNews: boolean;
  publishedAt?: string;
  /** Counted views, all-time rollup window. */
  views: number;
  /** All page-level reactions (ADR-106 derives counts at read time). */
  likes: number;
  comments: number;
};

/** One row of the owner-only Interactions feed. */
export type ProfileInteraction = {
  /** Stable key: source collection + row id. */
  id: string;
  kind: "reaction" | "comment";
  /** The reaction type when kind is "reaction" (like, love, clap, laugh). */
  reactionType?: string;
  pageTitle: string;
  href: string | null;
  createdAt: string;
  /** Comment body, when kind is "comment". */
  quote?: string;
};

/**
 * Contact details for the Public tab. Both phones separately — the
 * person-card DTO flattens them into one, and the contact card shows each
 * with its own label. All directory-safe fields.
 */
export type ProfileContact = {
  email?: string;
  phoneOffice?: string;
  phoneMobile?: string;
  office?: string;
};

export type ProfileFull = {
  person: ProfilePerson;
  contact: ProfileContact;
  /**
   * User-authored About text (`users.bio`), readable by every authenticated
   * reader and writable by the owner from the profile page. Absent when
   * unset — the About card leaves the page rather than drawing an empty one.
   */
  bio?: string;
  /** Server-rendered Graph photo as a data URL (240x240), when Entra has one. */
  photo?: string;
  /** Null when the index has no employee row for this person (or is down). */
  org: ProfileOrgDetails | null;
  reporting: ProfileReporting;
  /**
   * Viewer owns this profile. The Activity and Customization tabs render
   * only when true — and for a visitor the resolver never runs the feed
   * queries at all, so `authoredPages`/`interactions` are absent rather
   * than merely hidden.
   */
  isOwner: boolean;
  authoredPages?: ProfileAuthoredPage[];
  interactions?: ProfileInteraction[];
};
